<template>
  <main class="main main--request request">
    <div class="gradient-bg" />
    <module-spinner
      :active="!loaded"
      :floating="true"
    />
    <transition
      name="fade"
    >
      <div
        v-show="loaded"

        class="group mt request__wrapper"
      >
        <div class="request__heading">
          <h3 v-if="getContent(`title`)" class="h3">{{ getContent(`title`) }}</h3>
          <p v-if="getContent(`intro`)" class="p">{{ getContent(`intro`) }}</p>
          <p v-if="typeof getContent(`intro-continued`) === 'string'" class="p">{{ getContent(`intro-continued`) }}</p>
        </div>
        <module-detail-list
          v-if="requestState === `doctor-complete`"
          :items = "{
            'Patient Name': patient.full_name,
            'Procedure Type': reviewNomenclatureBySlugAndId(`procedure`, currentRequest.procedure),
            'Procedure Date': currentRequest.most_recent_procedure ? 'Patient’s most recent procedure' : currentRequest.date_of_procedure,
          }"
        />
        <module-form
          class="request-form"

          button="primary"
          form-id="request-form"
          :fields="fields"

          :action="action"
          :success="success"
          :fail="fail"
        />
      </div>
    </transition>
  </main>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

// Services
import { arrayLoop } from 'services/array';
import { americanizePhoneNumber } from 'services/string';

// Modules
import ModuleForm from 'modules/Form';
import ModuleSpinner from 'modules/Spinner';
import ModuleDetailList from 'modules/DetailList';

// Export
export default {
  name: `request`,

  // Components
  components: {
    ModuleForm,
    ModuleSpinner,
    ModuleDetailList,
  },

  // Data
  data() {
    return {
      loading: true,
      loadingRequest: true,
      content: {
        title: {
          'patient-new': `Submit a request for a review`,
          'patient-complete': `Submit Procedure Details`,
          'doctor-new': `Submit Procedure Details`,
          'doctor-complete': `Submit Procedure Details`,
        },
        intro: {
          'patient-new': `Fill out the form below and your dentist will get a request to upload your x-rays for review to a secure site; and you’ll get a confirmation.  It's that simple.`,
          'patient-complete': `Please fill out the form below`,
          'doctor-new': `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.`,
          'doctor-complete': `This patient has requested to upload radiographs and give additional information for the following procedure:`,
        },
        'intro-continued': {
          'patient-new': `Please provide your dentist with the most accurate information possible to ensure they submit the correct x-rays for review.  You have the power to make a formal request of your dentist and he/she must legally comply.`,
        },
        'field-procedure': {
          heading: {
            'patient-new': `Which procedure would you like reviewed?`,
            'patient-complete': `What procedure was done?`,
            'doctor-new': `What procedure did you carry out?`,
          },
          intro: {
            'patient-new': `Select the type of dental procedure you’d like verified from the list below.`,
          },
        },

        'field-was_rubber_dam_used': {
          heading: {
            'doctor-new': `Was a rubber dam used?`,
            'doctor-complete': `Was a rubber dam used?`,
            'patient-complete': `Was a rubber dam used?`,
          },
        },

        'field-most_recent_procedure': {
          heading: {
            'patient-new': `Was this your most recent procedure?`,
          },
        },

        'field-date_of_procedure': {
          label: {
            'patient-new': `When did you have this procedure?`,
          },
          placeholder: {
            'patient-new': `Procedure Date (exact of approximate)`,
          },
        },

        'field-doctor-first-name': {
          heading: {
            'patient-new': `Treating Doctor`,
          },
          intro: {
            'patient-new': `Who performed this procedure?`,
          },
          placeholder: {
            'patient-new': `First Name`,
          },
        },

        'field-doctor-last-name': {
          placeholder: {
            'patient-new': `Last Name`,
          },
        },

        'field-doctor-email': {
          placeholder: {
            'patient-new': `Email Address`,
          },
        },

        'field-doctor-number': {
          placeholder: {
            'patient-new': `Phone Number`,
          },
        },
        'field-confirm-procedure': {
          heading: {
            'doctor-complete': `Confirm Procedure Details`,
            'patient-complete': `Confirm Procedure Details`,
          },
          intro: {
            'doctor-complete': `What kind of procedure was done on this patient exactly?`,
            'patient-complete': `What kind of procedure was done exactly?`,
          },
        },
        'field-tooth-number': {
          heading: {
            'doctor-complete': `Tooth Number`,
            'patient-complete': `Tooth Number`,
          },
          intro: {
            'doctor-complete': `On which tooth was this procedure carried out?`,
            'patient-complete': `On which tooth was this procedure carried out?`,
          },
        },
      },
    };
  },

  computed: {
    ...mapGetters(`auth`, [
      `userRole`,
    ]),
    ...mapGetters(`forms`, [
      `getModel`,
    ]),
    ...mapGetters(`review`, [
      `reviewNomenclature`,
      `reviewNomenclatureBySlug`,
      `reviewNomenclatureBySlugAndId`,
      `reviewRequests`,
      `reviewRequestsById`,
    ]),
    model() {
      return this.getModel(`request-form`);
    },
    requestState() {
      const role = this.userRole.role;
      if (role === 1) return this.$route.name === `request-complete` ? `patient-complete` : `patient-new`;
      return this.$route.name === `request-complete` ? `doctor-complete` : `doctor-new`;
    },
    loaded() {
      return !this.loading && !this.loadingRequest;
    },
    currentRequest() {
      return this.reviewRequestsById(this.$route.params.id);
    },
    patient() {
      if(typeof this.currentRequest.patient === `undefined`) {
        return {};
      }

      const patient = this.currentRequest.patient;

      const firstName = patient.first_name;
      const lastName = patient.last_name;
      const fullName = `${firstName}${firstName && lastName ? ` ` : ``}${lastName}`;

      patient.full_name = fullName || ``;

      return patient;
    },
    fields() {
      if (this.loading) return {};

      const createFile = (label, index, required = false) => ({
        label: `${label === `Working Film` ? `Image` : `X-Ray`} ${index}`,
        type: `file`,
        // helper: `Upload a file and let us know when this X-Ray was taken.`,
        button: `primary`,
        validations: {
          required,
        },
      });

      const createDate = (label, index, required = false) => ({
        label: `${label} X-Ray ${index} date`,
        labelHidden: true,
        placeholder: `Enter date of ${label === `Working Film` ? `image` : `x-ray`}`,
        validations: {
          required,
        },
      });

      const createBoolean = () => ({
        type: `boolean`,
        label: `Add additional file`,
        labelHidden: true,
      });

      const createAdditionalFiles = (label, prefix, index, required = true) => {
        const additionalFiles = {};

        additionalFiles[`show_${prefix}_${index}`] = createBoolean();

        additionalFiles[`files_${prefix}_${index}`] = {
          ...createFile(label, index, required),
          conditional: model => model[`show_${prefix}_${index}`],
        };

        additionalFiles[`files_${prefix}_${index}_date`] = {
          ...createDate(label, index),
          conditional: model => model[`show_${prefix}_${index}`],
        };

        return additionalFiles;
      };

      const files = {
        // Pre-op
        files_pre_one: {
          heading: {
            heading: `Upload Pre-op X-Ray(s)`,
            level: `h5`,
          },
          intro: {
            copy: `Upload a file and let us know when this X-ray was taken`,
          },
          ...createFile(`Pre-op`, `one`, true),
        },
        files_pre_one_date: createDate(`Pre-op`, `one`, true),
        ...createAdditionalFiles(`Pre-op`, `pre`, `two`),

        // Post-op
        files_post_one: {
          heading: {
            heading: `Upload Post-op X-Ray(s)`,
            level: `h5`,
          },
          intro: {
            copy: `Upload a file and let us know when this X-ray was taken`,
          },
          ...createFile(`Post-op`, `one`, true),
        },
        files_post_one_date: createDate(`Post-op`, `one`, true),
        ...createAdditionalFiles(`Post-op`, `post`, `two`),

        // Working Film
        files_working_one_req: {
          heading: {
            heading: `Upload Working Film`,
            level: `h5`,
          },
          intro: {
            copy: `Upload a file and let us know when this X-ray was taken`,
          },
          ...createFile(`Working Film`, `one`, true),
        },
        files_working_one_req_date: createDate(`Working Film`, `one`, true),
        files_working_one_opt: createFile(`Working Film`, `one`, false),

        files_working_one_opt_date: createDate(`Working Film`, `one`, false),
        ...createAdditionalFiles(`Working Film`, `working`, `two`, false),
      };

      files.files_working_one_req.conditional = model => model.confirm_procedure !== 3;
      files.files_working_one_req_date.conditional = model => model.confirm_procedure !== 3;

      files.files_working_one_opt.conditional = model => model.confirm_procedure === 3;
      files.files_working_one_opt_date.conditional = model => model.confirm_procedure === 3;

      // Procedure
      const procedure = {
        heading: {
          heading: this.getContent(`field-procedure`, `heading`),
          level: `h5`,
        },
        intro: {
          copy: this.getContent(`field-procedure`, `intro`),
        },
        label: `Type of Procedure`,
        labelHidden: true,
        type: `select`,
        items: this.reviewNomenclatureBySlug(`procedure`),
        validations: {
          required: true,
        },
      };

      // When
      const was_rubber_dam_used = {
        heading: {
          heading: this.getContent(`field-was_rubber_dam_used`, `heading`),
          level: `h5`,
        },
        type: `radio`,
        items: [
          {
            option: `Yes`,
            value: true,
          },
          {
            option: `No`,
            value: `date`,
          },
        ],
        validations: {
          required: true,
        },
      };

      if (this.userRole.role === 1 && this.requestState === `patient-complete`) {
        const img = require(`images/rootCanalFaq/rubber-dam.png`);
        was_rubber_dam_used.tooltip = {
          title: `Click here for more info`,
          content: `
            <p>A Rubber Dam is considered the standard of care and should be used in all instances. It may be awkward, but this protects the area the doctor is working on and greatly reduces the potential for future infection.</p>
            <br />
            <img style="max-width: 100%;" src="${img}" alt="">
          `,
        };
      }

      // When
      const most_recent_procedure = {
        heading: {
          heading: this.getContent(`field-most_recent_procedure`, `heading`),
          level: `h5`,
        },
        type: `radio`,
        items: [
          {
            option: `Yes`,
            value: true,
          },
          {
            option: `No`,
            value: `date`,
          },
        ],
        validations: {
          required: true,
        },
      };

      // Enter Date
      const date_of_procedure = {
        label: this.getContent(`field-date_of_procedure`, `label`),
        labelHidden: true,
        placeholder: this.getContent(`field-date_of_procedure`, `placeholder`),
        conditional: model => model.most_recent_procedure === `date`,
      };

      // Doctor Info
      const first_name = {
        heading: {
          heading: this.getContent(`field-doctor-first-name`, `heading`),
          level: `h5`,
        },
        intro: {
          copy: this.getContent(`field-doctor-first-name`, `intro`),
        },
        placeholder: this.getContent(`field-doctor-first-name`, `placeholder`),
        label: `Doctor Information`,
        validations: {
          required: true,
        },
      };

      const last_name = {
        label: `Doctor Last Name`,
        placeholder: this.getContent(`field-doctor-last-name`, `placeholder`),
        labelHidden: true,
        validations: {
          required: true,
        },
      };

      const email = {
        label: `Enter Email`,
        labelHidden: true,
        type: `email`,
        errorLabel: `Email`,
        placeholder: this.getContent(`field-doctor-email`, `placeholder`),
        validations: {
          required: true,
          email: true,
        },
      };
      const phone_number = {
        label: `Doctor Phone Number`,
        placeholder: this.getContent(`field-doctor-number`, `placeholder`),
        labelHidden: true,
        validations: {
          required: true,
        },
      };

      // Doctor Upload

      // Confirm Procedure
      const confirm_procedure = {
        heading: {
          heading: this.getContent(`field-confirm-procedure`, `heading`),
          level: `h5`,
        },
        intro: {
          copy: this.getContent(`field-confirm-procedure`, `intro`),
        },
        type: `radio`,
        items: [
          {
            option: `Endodontics  (Root Canal, Retreatment, Incomplete RCT ie. Emergency palliative)`,
            value: 3,
          },
          {
            option: `Restorative  (Coming Soon)`,
            value: 1,
            disabled: true,
          },
          {
            option: `Fixed Pros. (Coming Soon)`,
            value: 2,
            disabled: true,
          },
        ],
        validations: {
          required: true,
        },
      };

      const tooth_number = {
        heading: {
          heading: this.getContent(`field-tooth-number`, `heading`),
          level: `h5`,
        },
        intro: {
          copy: this.getContent(`field-tooth-number`, `intro`),
        },
        type: `Number`,
        label: `Tooth Number`,
        labelHidden: true,
        validations: {
          required: true,
          min: 1,
          max: 32,
        },
      };

      let fields = {};


      if (this.requestState === `patient-new`) {
        fields = {
          procedure,
          most_recent_procedure,
          date_of_procedure,
          first_name,
          last_name,
          email,
          phone_number,
        };
      } else if (this.requestState === `doctor-complete`) {
        fields = {
          confirm_procedure,
          tooth_number,
          was_rubber_dam_used,
          ...files,
        };
      } else if (this.requestState === `patient-complete`) {
        if ( this.userRole.role === 1 ) {
          fields = {
            confirm_procedure,
            was_rubber_dam_used,
            ...files,
          };
        } else {
          fields = {
            confirm_procedure,
            tooth_number,
            was_rubber_dam_used,
            ...files,
          };
        }

      }

      return fields;
    },

  },

  // Methods
  methods: {
    ...mapActions(`forms`, [
      `updateValue`,
    ]),
    ...mapActions(`review`, [
      `getNomenclature`,
      `requestReviewId`,
      `requestReviewPostInfo`,
      `requestReviewUploadImage`,
      `getReviewRequestsDoctor`,
      `getReviewRequestsDoctorById`,
    ]),
    action() {
      if(this.requestState === `patient-new`) {
        return this.actionPatientRequest();
      } else if(this.requestState === `patient-complete`) {
        return this.actionPatientUpload();
      }
      return this.actionDentist();
    },
    actionPatientRequest() {
      const model = {
        procedure: this.model.procedure,
        treating_doctor: {
          first_name: this.model.first_name,
          last_name: this.model.last_name,
          email: this.model.email,
          phone_number: americanizePhoneNumber(this.model.phone_number),
        },
      };

      if (this.model.most_recent_procedure === `date`) {
        model.date_of_procedure = this.model.date_of_procedure;
      }
      else {
        model.most_recent_procedure = true;
      }
      return this.requestReviewId(model);
    },
    actionPatientUpload() {
      return this.actionBaseAnswer();
    },
    actionDentist() {
      return this.actionBaseAnswer();
    },
    actionBaseAnswer() {
      return new Promise((resolve, reject) => {

        const model = {
          id: this.$route.params.id,
          tooth_number: this.model.tooth_number,
          was_rubber_dam_used: this.model.was_rubber_dam_used,
          procedure_type: this.model.confirm_procedure,
        };

        const promises = [];

        const categoryMap = {
          pre: 1,
          post: 2,
          working: 3,
        };

        arrayLoop(this.model, (key, value) => {
          if (/^files_/.test(key)) {
            if (value && !/_date/.test(key)) {
              promises.push(this.requestReviewUploadImage({
                image: value,
                id: model.id,
                date: this.model[`${key}_date`],
                category: categoryMap[key.split(`_`)[1]],
              }));
            }
          }
        });

        return Promise.all(promises)
          .then(() => {
            this.requestReviewPostInfo(model)
              .then(() => {
                resolve();
              });
          })
          .catch(err => {
            console.warn(`Err here`, err);
            reject(err);
          });
      });
    },
    success(response) {
      const getSuccessPage = () => {
        if (this.userRole.role === 1) {
          if(this.requestState === `patient-complete`) {
            return `patient-xray-upload`;
          }

          return `patient-procedure`;
        }

        return `${this.userRole.role === 2 ? `doctor` : `reviewer`}-xray-upload`;
      };

      const routeToConfig = {
        method: `push`,
        routeTo: {
          name: `success`,
          params: {
            page: getSuccessPage(),
          },
        },
      };

      if(typeof response !== undefined && this.requestState === `patient-new`) {
        const resolvedRoute = this.$router.resolve({
          name: `dashboard-review`,
          params: { id: response.id },
        });
        routeToConfig.method = `replace`;
        routeToConfig.routeTo = {
          name: `review-payment-request`,
          params: { id: response.order.id, redirect_to: resolvedRoute.href },
        };
      }

      setTimeout(() => {
        this.$router[routeToConfig.method](routeToConfig.routeTo);
      }, 1000);
    },
    fail(fail) {
      console.warn(`Fail`, fail);
    },
    getContent(string, field) {
      let object = this.content[string] || {};

      if (field) object = object[field] || {};

      return object[this.requestState] || {};
    },
  },

  // Lifecycle
  created() {
    this.getNomenclature()
      .then(() => {
        this.loading = false;

        this.$nextTick(() => {
          // console.log(sessionStorage.getItem(`procedure`));
          this.updateValue({
            form: `request-form`,
            field: `procedure`,
            value: sessionStorage.getItem(`procedure`),
          });

          sessionStorage.removeItem(`procedure`);
        });
      });
    if (this.requestState === `doctor-complete` || this.requestState === `patient-complete`) {
      this.getReviewRequestsDoctorById(this.$route.params.id)
        .then(() => {
          this.loadingRequest = false;
        })
        .catch((err) => {
          if(err && err.status === 404) {
            this.$router.replace({
              name: `notFound`,
            });
          }
        });
    }
    else this.loadingRequest = false;
  },
};
</script>



// WEBPACK FOOTER //
// Request.vue?2af75fa6